/**
 * VerifierLanding.js
 *
 * Placeholder landing page for users with the Verifier role.
 *
 * In MR 1, Verifiers don't have any audit access yet. This page just
 * acknowledges them and explains that the verification feature is coming.
 *
 * In MR 2 (next release), this page will be replaced with:
 *   - A list of audits the Verifier has been granted access to (with expiry)
 *   - Read-only review interface
 *   - Time-bounded access enforcement
 */

import React from 'react';
import {
  Box, Container, Typography, Paper, useTheme, alpha,
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useAuth } from '../auth/AuthContext';

const VerifierLanding = () => {
  const theme = useTheme();
  const { currentUser, tenantName } = useAuth();

  return (
    <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
      <Paper
        elevation={2}
        sx={{
          p: 6,
          borderRadius: 3,
          textAlign: 'center',
          background: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VerifiedUserOutlinedIcon
              sx={{ fontSize: 44, color: theme.palette.primary.main }}
            />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Welcome, Verifier
        </Typography>

        {currentUser?.email && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Signed in as {currentUser.email}
            {tenantName ? ` · ${tenantName}` : ''}
          </Typography>
        )}

        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
            You don't have any audits to verify yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The tenant administrator will grant you access to specific audits
            for verification, with a defined access window. Once you've been
            invited, the audits available for your review will appear here.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default VerifierLanding;